import { useCallback, useMemo, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faKeyboard, faMouse, faHandBackPointUp } from '@fortawesome/pro-light-svg-icons';
import { faGamepadAlt as faGamepadAltDuotone } from '@fortawesome/duotone-light-svg-icons/faGamepadAlt';
import { faKeyboard as faKeyboardDuotone } from '@fortawesome/duotone-light-svg-icons/faKeyboard';
import { faMouse as faMouseDuotone } from '@fortawesome/duotone-light-svg-icons/faMouse';
import {
  faBootHeeled,
  faChevronLeft,
  faClose,
  faGamepadAlt as faGamepadAltSolid,
  faPause,
  faPlay,
} from '@fortawesome/pro-solid-svg-icons';
import clsx from 'clsx';
import type { ConnectedPlayerInfo } from '../../../coordinators/peer/hooks';
import { ConnectionType } from '../../../components/widgets/Connection';
import { ConnectionV2 } from '../../../components/widgets/ConnectionV2';
import { BeaconGamepadButtons } from './BeaconGamepadButtons';
import { BeaconShareMenu } from './BeaconShareMenu';
import type { PeerCoordinator } from '../../../coordinators/peer/PeerCoordinator';
import type { BeaconCoordinator } from '../../../coordinators/beacon/BeaconCoordinator';
import type { BeaconInputConfig } from '../../../coordinators/beacon/types';

type BeaconControllerPanelProps = {
  player?: ConnectedPlayerInfo;
  name: string;
  isGlobalPaused: boolean;
  hostGamepadIds: number[];
  peerCoordinator: PeerCoordinator;
  globalInputConfig?: BeaconInputConfig;
  beaconCoordinator?: BeaconCoordinator;
};

const BUTTON_CLASSES = {
  base: 'flex items-center justify-center rounded',
  size: 'size-12',
  active: 'bg-emerald-400 hover:bg-emerald-400/80 active:bg-emerald-400/60 text-darkest',
  inactive: 'bg-slate-600 hover:bg-slate-600/80 active:bg-slate-600/60',
};

const getConnectionType = (connType: string | undefined): ConnectionType => {
  switch (connType) {
    case 'host': return 'Local';
    case 'relay': return 'Relay';
    case 'prflx': case 'srflx': return 'P2P';
    default: return 'Unknown';
  }
};

export const BeaconControllerPanel = ({
  player,
  name,
  isGlobalPaused,
  hostGamepadIds,
  peerCoordinator,
  globalInputConfig,
  beaconCoordinator,
}: BeaconControllerPanelProps) => {
  const isGlobal = !player;
  const playerId = player?.peerId;

  const [mobileExpanded, setMobileExpanded] = useState(false);
  const [mobileKbmExpanded, setMobileKbmExpanded] = useState(false);
  const [mobileGamepadExpanded, setMobileGamepadExpanded] = useState(false);

  const ping = useMemo(() => (player?.stats?.rtt ?? 0) * 1000, [player?.stats?.rtt]);
  const connType = useMemo(() => getConnectionType(player?.stats?.connectionType), [player?.stats?.connectionType]);
  const isAfk = player?.afk ?? false;
  const isPaused = isGlobal ? isGlobalPaused : (player?.paused ?? false);
  const isKeyboardEnabled = isGlobal ? (globalInputConfig?.keyboard ?? true) : (player?.permissions.keyboard ?? false);
  const isMouseEnabled = isGlobal ? (globalInputConfig?.mouse ?? true) : (player?.permissions.mouse ?? false);
  const isTouchEnabled = isGlobal ? (globalInputConfig?.touch ?? true) : (player?.permissions.touch ?? false);
  const playerSlots = player?.gamepadSlots ?? [];

  const handleSlotToggle = useCallback(
    (num: number, enabled: boolean) => {
      if (isGlobal || !playerId) return;
      if (enabled) {
        peerCoordinator.assignGamepadSlot(playerId, num - 1);
      } else {
        peerCoordinator.removeGamepadSlot(playerId, num - 1);
      }
    },
    [isGlobal, playerId, peerCoordinator],
  );

  const handleGamepadAccessChange = useCallback(
    (enabled: boolean) => {
      if (!playerId) return;
      peerCoordinator.setGamepadAccess(playerId, enabled);
    },
    [playerId, peerCoordinator],
  );

  const handleKeyboardToggle = useCallback(() => {
    if (isGlobal) {
      beaconCoordinator?.setKeyboardEnabled(!isKeyboardEnabled);
      return;
    }
    if (!playerId) return;
    peerCoordinator.updatePeerPermissions(playerId, { keyboard: !player?.permissions.keyboard });
  }, [isGlobal, isKeyboardEnabled, playerId, player?.permissions.keyboard, peerCoordinator, beaconCoordinator]);

  const handleMouseToggle = useCallback(() => {
    if (isGlobal) {
      beaconCoordinator?.setMouseEnabled(!isMouseEnabled);
      return;
    }
    if (!playerId) return;
    peerCoordinator.updatePeerPermissions(playerId, { mouse: !player?.permissions.mouse });
  }, [isGlobal, isMouseEnabled, playerId, player?.permissions.mouse, peerCoordinator, beaconCoordinator]);

  const handleTouchToggle = useCallback(() => {
    if (isGlobal) {
      beaconCoordinator?.setTouchEnabled(!isTouchEnabled);
      return;
    }
    if (!playerId) return;
    peerCoordinator.updatePeerPermissions(playerId, { touch: !player?.permissions.touch });
  }, [isGlobal, isTouchEnabled, playerId, player?.permissions.touch, peerCoordinator, beaconCoordinator]);

  const handleKick = useCallback(() => {
    if (isGlobal) {
      peerCoordinator.disconnectAllPeers();
    } else if (playerId) {
      peerCoordinator.kickPeer(playerId);
    }
  }, [isGlobal, playerId, peerCoordinator]);

  const handlePauseToggle = useCallback(() => {
    if (isGlobal) {
      peerCoordinator.setGlobalPaused(!isGlobalPaused);
    } else {
      if (isGlobalPaused) return;
      if (playerId) {
        peerCoordinator.setPeerPaused(playerId, !(player?.paused ?? false));
      }
    }
  }, [isGlobal, isGlobalPaused, playerId, player?.paused, peerCoordinator]);

  return (
    <>
      <div className='flex flex-1'>
        {/* Desktop */}
        <div
          className={clsx(
            'border-primary hidden flex-col justify-between gap-2 rounded-lg border-2 bg-gray-800 p-2 lg:flex',
            !isGlobal && 'w-full relative',
            !isGlobal && isAfk &&
              'after:pointer-events-none after:absolute after:inset-0 after:rounded-lg after:bg-gray-800/60 after:bg-opacity-50 after:content-[""]',
          )}
        >
          <div className='flex items-center justify-between'>
            <span className='text-white'>{name}</span>
            {!isGlobal && <ConnectionV2 ping={ping} type={connType} />}
          </div>
          <div className='flex gap-2'>
            <div className={clsx(
              'flex flex-1 items-center',
              isGlobal ? 'gap-10' : 'border-primary justify-between gap-2 rounded border-2 p-2',
            )}>
              <BeaconGamepadButtons
                isGlobal={isGlobal}
                isAfk={isAfk}
                playerSlots={playerSlots}
                hostGamepadIds={hostGamepadIds}
                buttonClasses={BUTTON_CLASSES}
                onSlotToggle={handleSlotToggle}
                onGamepadAccessChange={handleGamepadAccessChange}
              />
              <div className='flex gap-2'>
                <button
                  className={clsx(
                    BUTTON_CLASSES.base, BUTTON_CLASSES.size,
                    isKeyboardEnabled ? BUTTON_CLASSES.active : BUTTON_CLASSES.inactive,
                  )}
                  onClick={handleKeyboardToggle}
                >
                  <FontAwesomeIcon icon={faKeyboard} className='size-8' />
                </button>
                <button
                  className={clsx(
                    BUTTON_CLASSES.base, BUTTON_CLASSES.size,
                    isMouseEnabled ? BUTTON_CLASSES.active : BUTTON_CLASSES.inactive,
                  )}
                  onClick={handleMouseToggle}
                >
                  <FontAwesomeIcon icon={faMouse} className='size-6' />
                </button>
                <button
                  className={clsx(
                    BUTTON_CLASSES.base, BUTTON_CLASSES.size,
                    isTouchEnabled ? BUTTON_CLASSES.active : BUTTON_CLASSES.inactive,
                  )}
                  onClick={handleTouchToggle}
                >
                  <FontAwesomeIcon icon={faHandBackPointUp} className='size-6' />
                </button>
              </div>
            </div>
            {!isGlobal && (
              <div className='flex items-center gap-2'>
                <button
                  className={clsx('bg-primary active:bg-primary/60 hover:bg-primary/80 rounded', BUTTON_CLASSES.size)}
                  onClick={handleKick}
                >
                  <FontAwesomeIcon icon={faBootHeeled} className='size-6 -rotate-[30deg] text-orange-600' />
                </button>
                <button
                  className={clsx(
                    BUTTON_CLASSES.base, BUTTON_CLASSES.size,
                    isGlobalPaused ? 'bg-slate-600' : 'bg-primary active:bg-primary/60 hover:bg-primary/80 rounded',
                    'group',
                  )}
                  onClick={handlePauseToggle}
                  disabled={!isGlobal && isGlobalPaused}
                >
                  <FontAwesomeIcon icon={isPaused ? faPlay : faPause} className='size-8 text-amber-400 group-disabled:text-slate-400' />
                </button>
                <div className={clsx(
                  'flex h-12 items-center justify-center rounded px-4 py-1 text-3xl',
                  isAfk ? 'z-10 bg-purple-900 text-purple-400' : 'border-2 border-slate-600 text-slate-600',
                )}>
                  AFK
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Mobile */}
        {isGlobal ? (
          <div className='border-primary bg-primary flex w-full gap-2 rounded-lg border-2 lg:hidden'>
            <div className='flex flex-1 items-center justify-between rounded-lg bg-gray-800 p-2'>
              <span className='w-20 text-center'>{name}</span>
              <div className='flex gap-2'>
                <button className='bg-primary size-12 rounded-lg md:size-16'>
                  <FontAwesomeIcon
                    icon={faGamepadAltSolid}
                    className='text-darkest size-10 md:size-12'
                    style={{ '--fa-secondary-color': '#00d492', '--fa-secondary-opacity': '1' } as React.CSSProperties}
                  />
                </button>
                <button className='bg-primary relative flex size-12 items-center justify-center rounded-lg p-2 md:size-16'>
                  <FontAwesomeIcon icon={faKeyboardDuotone} className='text-darkest size-10 md:size-12'
                    style={{ '--fa-secondary-color': '#00d492', '--fa-secondary-opacity': '1' } as React.CSSProperties} />
                  <FontAwesomeIcon icon={faMouseDuotone} className='text-darkest absolute bottom-1 right-0 size-6 md:bottom-2 md:size-7'
                    style={{ '--fa-secondary-color': '#00d492', '--fa-secondary-opacity': '1' } as React.CSSProperties} />
                </button>
              </div>
            </div>
            <div className='flex items-center gap-2 rounded-lg pr-1.5'>
              <button
                className='flex size-12 items-center justify-center rounded-lg bg-amber-600/40 active:bg-amber-600/60 md:size-16'
                onClick={() => peerCoordinator.setGlobalPaused(!isGlobalPaused)}
              >
                <FontAwesomeIcon icon={isPaused ? faPlay : faPause} className='size-8 text-amber-400 md:size-10' />
              </button>
              <button
                className='flex size-12 items-center justify-center rounded-lg bg-orange-600/60 active:bg-orange-600/40 md:size-16'
                onClick={() => peerCoordinator.disconnectAllPeers()}
              >
                <FontAwesomeIcon icon={faBootHeeled} className='size-6 -rotate-[30deg] text-orange-600 md:size-8' />
              </button>
            </div>
          </div>
        ) : (
          <div className='bg-primary flex w-full rounded-lg p-0.5 lg:hidden'>
            <div className='relative flex w-full items-center gap-1 overflow-hidden rounded-lg'>
              <div className='bg-darkest flex flex-1 flex-col rounded-md px-2 py-0.5'>
                <span className='text-lg text-white'>{name}</span>
                <ConnectionV2 ping={ping} type={connType} />
              </div>
              <div className={clsx(
                'bg-darkest absolute right-0 flex h-full w-fit items-center rounded-r-md border-l-4 border-amber-400 transition-all duration-700 ease-in-out',
                mobileExpanded ? 'translate-x-0' : 'translate-x-[85%]',
              )}>
                <div className='flex h-full items-center px-1'>
                  {mobileKbmExpanded || mobileGamepadExpanded ? (
                    <button onClick={() => { setMobileKbmExpanded(false); setMobileGamepadExpanded(false); }} className='flex flex-shrink-0 items-center pr-1'>
                      <FontAwesomeIcon icon={faClose} className='size-8 text-amber-400' />
                    </button>
                  ) : (
                    <button onClick={() => setMobileExpanded(!mobileExpanded)} className='flex flex-shrink-0 items-center pr-1'>
                      <FontAwesomeIcon icon={faChevronLeft} className={clsx('size-8 text-amber-400 transition-transform duration-700', mobileExpanded ? 'rotate-180' : 'rotate-0')} />
                    </button>
                  )}
                  <div className='flex h-full items-center justify-center gap-1 md:gap-2'>
                    {mobileGamepadExpanded ? (
                      <BeaconGamepadButtons
                        isGlobal={false}
                        isAfk={isAfk}
                        playerSlots={playerSlots}
                        hostGamepadIds={hostGamepadIds}
                        buttonClasses={BUTTON_CLASSES}
                        onSlotToggle={handleSlotToggle}
                        onGamepadAccessChange={handleGamepadAccessChange}
                      />
                    ) : mobileKbmExpanded ? (
                      <div className='flex items-center gap-2 px-7'>
                        <button
                          className={clsx('flex size-12 items-center justify-center rounded-lg p-2', isKeyboardEnabled ? 'bg-emerald-400 active:bg-emerald-400/80' : 'bg-primary active:bg-primary/80')}
                          onClick={handleKeyboardToggle}
                        >
                          <FontAwesomeIcon icon={faKeyboard} className='text-darkest size-10' />
                        </button>
                        <button
                          className={clsx('flex size-12 items-center justify-center rounded-lg p-2', isMouseEnabled ? 'bg-emerald-400 active:bg-emerald-400/80' : 'bg-primary active:bg-primary/80')}
                          onClick={handleMouseToggle}
                        >
                          <FontAwesomeIcon icon={faMouse} className='text-darkest size-8' />
                        </button>
                        <button
                          className={clsx('flex size-12 items-center justify-center rounded-lg p-2', isTouchEnabled ? 'bg-emerald-400 active:bg-emerald-400/80' : 'bg-primary active:bg-primary/80')}
                          onClick={handleTouchToggle}
                        >
                          <FontAwesomeIcon icon={faHandBackPointUp} className='text-darkest size-8' />
                        </button>
                      </div>
                    ) : (
                      <>
                        <button className='bg-primary active:bg-primary/80 flex size-12 items-center justify-center rounded-lg p-2' onClick={() => setMobileGamepadExpanded(true)}>
                          <FontAwesomeIcon icon={faGamepadAltDuotone} className='text-darkest size-10'
                            style={{ '--fa-secondary-color': playerSlots.length > 0 ? '#00d492' : '#2D618C', '--fa-secondary-opacity': '1' } as React.CSSProperties} />
                        </button>
                        <button className='bg-primary relative flex size-12 items-center justify-center rounded-lg p-2' onClick={() => setMobileKbmExpanded(true)}>
                          <FontAwesomeIcon icon={faKeyboardDuotone} className='text-darkest size-10'
                            style={{ '--fa-secondary-color': isKeyboardEnabled ? '#00d492' : '#2D618C', '--fa-secondary-opacity': '1' } as React.CSSProperties} />
                          <FontAwesomeIcon icon={faMouseDuotone} className='text-darkest absolute bottom-1 right-0 size-6'
                            style={{ '--fa-secondary-color': isMouseEnabled ? '#00d492' : '#2D618C', '--fa-secondary-opacity': '1' } as React.CSSProperties} />
                        </button>
                        <button
                          disabled={isGlobalPaused}
                          className={clsx('group flex size-12 items-center justify-center rounded-lg p-2', isGlobalPaused ? 'bg-slate-600' : 'bg-primary active:bg-primary/80')}
                          onClick={handlePauseToggle}
                        >
                          <FontAwesomeIcon icon={isPaused ? faPlay : faPause} className='text-darkest size-8 group-disabled:text-slate-400' />
                        </button>
                      </>
                    )}
                  </div>
                </div>
                {!mobileGamepadExpanded && (
                  <div className='border-primary bg-primary flex h-full items-center border-l-2'>
                    <button className='h-full w-12 bg-orange-600/60 p-2 active:bg-orange-600/40' onClick={handleKick}>
                      <FontAwesomeIcon icon={faBootHeeled} className='size-6 -rotate-[30deg] text-orange-600 md:size-8' />
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
      {isGlobal && (
        <div className='hidden flex-col gap-1 lg:flex'>
          <button
            className='bg-primary hover:bg-primary/80 active:bg-primary/60 group flex items-center gap-3 rounded px-4 py-1 disabled:bg-slate-600 disabled:text-slate-400'
            onClick={() => peerCoordinator.disconnectAllPeers()}
          >
            <FontAwesomeIcon icon={faBootHeeled} className='size-4 -rotate-[30deg] text-orange-600 group-disabled:text-slate-400' />
            <span>Kick All</span>
          </button>
          <button
            className='bg-primary hover:bg-primary/80 active:bg-primary/60 flex items-center gap-2 rounded px-3 py-1'
            onClick={() => peerCoordinator.setGlobalPaused(!isGlobalPaused)}
          >
            <FontAwesomeIcon icon={isGlobalPaused ? faPlay : faPause} className='size-6 text-amber-400' />
            <span>{isGlobalPaused ? 'Unpause All' : 'Pause All'}</span>
          </button>
          <BeaconShareMenu className='!size-auto' position='top' />
        </div>
      )}
    </>
  );
};
